import React, { useState } from 'react';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import { authApi } from '../../services/authApi';
import './AuthForm.css';

interface ForgotPasswordFormProps {
  onSwitchToLogin: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await authApi.forgotPassword(email);
      
      if (response.success) {
        setIsSuccess(true);
        setMessage('If an account exists with this email, a password reset link has been sent. Please check your inbox.');
        setEmail('');
      } else {
        setIsSuccess(false);
        setMessage(response.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      setIsSuccess(false);
      // Still show success message to prevent email enumeration
      setMessage('If an account exists with this email, a password reset link has been sent. Please check your inbox.');
      setIsSuccess(true);
      setEmail('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (message) setMessage(null);
  };

  return (
    <div className="auth-form-container">
      <h2 className="auth-form-title">Forgot Password</h2>
      
      {message && (
        <div className={isSuccess ? 'auth-success-message' : 'auth-error-message'}>
          {message}
        </div>
      )}

      {!isSuccess && (
        <>
          <p style={{ color: 'var(--ut-text-secondary)', marginBottom: '20px', fontSize: 'var(--ut-font-body-sm)' }}>
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            <FormGroup>
              <FormLabel htmlFor="email">
                Email
              </FormLabel>
              <FormInput
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                required
                disabled={isSubmitting}
                placeholder="Enter your email"
              />
            </FormGroup>

            <FormButton
              type="submit"
              disabled={isSubmitting || !email}
              variant="primary"
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </FormButton>
          </form>
        </>
      )}

      <div className="auth-switch-container">
        <p>
          Remember your password?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="auth-switch-button"
            disabled={isSubmitting}
          >
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};
